import React, { useCallback, useEffect, useState } from "react";

import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { Add, Edit, Search } from "@mui/icons-material";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";

import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";

import { AddOrEditAttendanceBonusRule } from "../../../services/asaiadmin";

/* =========================================================
   BREADCRUMB
   ========================================================= */

const BCrumb = [
  {
    to: "/",
    title: "ASAI Admin",
  },
  {
    title: "Attendance Bonus Rule Master",
  },
];

/* =========================================================
   FIXED GRADE OPTIONS
   ========================================================= */

const gradeOptions = [
  { value: "0", label: "0" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "D", label: "D" },
  { value: "E", label: "E" },
];

const ruleTypeOptions = [
  { value: "LEAVE_WEEKS", label: "Leave in Weeks" },
  { value: "ADVANCE_LEAVE_ONLY", label: "Advance Leave Only" },
  { value: "PERFECT_ATTENDANCE", label: "Perfect Attendance" },
];

const emptyForm = {
  id: 0,
  gradeCode: "",
  ruleType: "LEAVE_WEEKS",
  leaveWeeksRequired: "1",
  requiredAdvanceLeaveCount: "1",
  allowedOtherLeaveCount: "0",
  evaluationOrder: "1",
  criteriaDescription: "",
  isActive: true,
};

/* =========================================================
   RESPONSE HELPERS
   ========================================================= */

const getResponseRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

const toNullableNumber = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);

const normalizeRule = (item) => {
  const activeValue = item.isActive ?? item.IsActive ?? true;

  return {
    id: item.id ?? item.Id ?? 0,

    gradeCode: String(item.gradeCode ?? item.GradeCode ?? ""),

    ruleType: (item.ruleType ?? item.RuleType ?? "LEAVE_WEEKS").toUpperCase(),

    leaveWeeksRequired:
      item.leaveWeeksRequired ?? item.LeaveWeeksRequired ?? null,

    requiredAdvanceLeaveCount:
      item.requiredAdvanceLeaveCount ?? item.RequiredAdvanceLeaveCount ?? null,

    allowedOtherLeaveCount:
      item.allowedOtherLeaveCount ?? item.AllowedOtherLeaveCount ?? null,

    evaluationOrder: item.evaluationOrder ?? item.EvaluationOrder ?? 1,

    criteriaDescription:
      item.criteriaDescription ?? item.CriteriaDescription ?? "",

    isActive:
      activeValue === true ||
      activeValue === 1 ||
      activeValue === "1" ||
      String(activeValue).toLowerCase() === "true",
  };
};

const getResult = (response) => {
  const data = response?.data;

  return Array.isArray(data) ? data[0] ?? {} : data ?? {};
};

const getMessage = (result) => result?.message ?? result?.Message ?? "";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ??
  error?.response?.data?.Message ??
  error?.response?.data?.title ??
  error?.message ??
  fallback;

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const AttendanceBonusRuleMaster = () => {
  const [rules, setRules] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [pageError, setPageError] = useState("");

  /* =========================================================
     VALIDATION
     ========================================================= */

  const validationSchema = Yup.object({
    gradeCode: Yup.string()
      .trim()
      .required("Grade is required.")
      .max(5, "Grade cannot exceed 5 characters.")
      .matches(/^[A-Z0-9]+$/, "Use uppercase letters and numbers only."),

    ruleType: Yup.string()
      .required("Rule type is required.")
      .oneOf(
        ["LEAVE_WEEKS", "ADVANCE_LEAVE_ONLY", "PERFECT_ATTENDANCE"],
        "Select a valid rule type.",
      ),

    leaveWeeksRequired: Yup.number()
      .nullable()
      .when("ruleType", {
        is: "LEAVE_WEEKS",
        then: (schema) =>
          schema
            .required("Leave weeks are required.")
            .integer("Enter a whole number.")
            .min(1, "Minimum is 1 week.")
            .max(4, "A month has exactly 4 evaluated weeks."),
        otherwise: (schema) => schema.nullable(),
      }),

    requiredAdvanceLeaveCount: Yup.number()
      .nullable()
      .when("ruleType", {
        is: "ADVANCE_LEAVE_ONLY",
        then: (schema) =>
          schema
            .required("Advance leave count is required.")
            .integer("Enter a whole number.")
            .min(1, "Minimum count is 1."),
        otherwise: (schema) => schema.nullable(),
      }),

    allowedOtherLeaveCount: Yup.number()
      .nullable()
      .when("ruleType", {
        is: "ADVANCE_LEAVE_ONLY",
        then: (schema) =>
          schema
            .required("Allowed other leave count is required.")
            .integer("Enter a whole number.")
            .min(0, "Cannot be negative."),
        otherwise: (schema) => schema.nullable(),
      }),

    evaluationOrder: Yup.number()
      .required("Evaluation order is required.")
      .integer("Enter a whole number.")
      .min(1, "Minimum order is 1.")
      .max(255, "Maximum order is 255."),

    criteriaDescription: Yup.string()
      .trim()
      .required("Criteria description is required.")
      .max(500, "Criteria description cannot exceed 500 characters."),

    isActive: Yup.boolean(),
  });

  /* =========================================================
     GET RULES
     ========================================================= */

  const getRules = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      const response = await AddOrEditAttendanceBonusRule({
        action: "GET",
      });

      setRules(getResponseRows(response).map(normalizeRule));
    } catch (error) {
      console.error("Error loading attendance bonus rules:", error);

      setPageError(
        getErrorMessage(error, "Unable to load attendance bonus rules."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getRules();
  }, [getRules]);

  /* =========================================================
     FORMIK
     ========================================================= */

  const formik = useFormik({
    initialValues: emptyForm,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,

    onSubmit: async (values, helpers) => {
      try {
        setSaving(true);

        const payload = {
          action: isEdit ? "UPDATE" : "INSERT",

          id: isEdit ? Number(values.id) : null,

          gradeCode: values.gradeCode,

          ruleType: values.ruleType,

          leaveWeeksRequired:
            values.ruleType === "LEAVE_WEEKS"
              ? toNullableNumber(values.leaveWeeksRequired)
              : values.ruleType === "PERFECT_ATTENDANCE"
              ? 0
              : null,

          requiredAdvanceLeaveCount:
            values.ruleType === "ADVANCE_LEAVE_ONLY"
              ? toNullableNumber(values.requiredAdvanceLeaveCount)
              : null,

          allowedOtherLeaveCount:
            values.ruleType === "ADVANCE_LEAVE_ONLY"
              ? toNullableNumber(values.allowedOtherLeaveCount)
              : null,

          evaluationOrder: Number(values.evaluationOrder),

          criteriaDescription: values.criteriaDescription.trim(),

          // New rules are active by default. Edits preserve status.
          isActive: isEdit ? values.isActive : true,
        };

        console.log("Attendance Bonus Rule Payload:", payload);

        const response = await AddOrEditAttendanceBonusRule(payload);

        const result = getResult(response);

        if (Number(result?.status ?? result?.Status) === 0) {
          toast.warning(
            getMessage(result) || "Unable to save attendance bonus rule.",
          );

          return;
        }

        toast.success(
          getMessage(result) ||
            (isEdit
              ? "Attendance bonus rule updated successfully."
              : "Attendance bonus rule added successfully."),
        );

        helpers.resetForm({
          values: emptyForm,
        });

        helpers.setTouched({});
        setIsEdit(false);
        setPage(0);

        await getRules();
      } catch (error) {
        console.error("Error saving attendance bonus rule:", error);

        toast.error(
          getErrorMessage(error, "Unable to save attendance bonus rule."),
        );
      } finally {
        setSaving(false);
        helpers.setSubmitting(false);
      }
    },
  });

  /* =========================================================
     AVAILABLE GRADE OPTIONS
     Prevents duplicate grade codes in Add mode.
     ========================================================= */

  // const usedGradeCodes = new Set(
  //   rules
  //     .filter((rule) => !isEdit || rule.id !== Number(formik.values.id))
  //     .map((rule) => rule.gradeCode)
  // );

  // const availableGradeCodes = gradeOptions.filter(
  //   (grade) => !usedGradeCodes.has(grade.value)
  // );

  /* =========================================================
     RESET
     ========================================================= */

  const resetForm = () => {
    formik.resetForm({
      values: emptyForm,
    });

    formik.setTouched({});
    setIsEdit(false);
  };

  /* =========================================================
     EDIT
     ========================================================= */

  const handleEdit = (item) => {
    const editData = {
      id: item.id,
      gradeCode: item.gradeCode,
      ruleType: item.ruleType,

      leaveWeeksRequired:
        item.leaveWeeksRequired == null ? "" : String(item.leaveWeeksRequired),

      requiredAdvanceLeaveCount:
        item.requiredAdvanceLeaveCount == null
          ? ""
          : String(item.requiredAdvanceLeaveCount),

      allowedOtherLeaveCount:
        item.allowedOtherLeaveCount == null
          ? ""
          : String(item.allowedOtherLeaveCount),

      evaluationOrder: String(item.evaluationOrder),

      criteriaDescription: item.criteriaDescription,

      isActive: item.isActive,
    };

    formik.setValues(editData);
    formik.setTouched({});
    setIsEdit(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
     STATUS UPDATE
     ========================================================= */

  const handleStatusChange = async (item, nextStatus) => {
    try {
      await AddOrEditAttendanceBonusRule({
        action: "UPDATE",
        id: item.id,
        isActive: nextStatus,
      });

      toast.success(
        nextStatus
          ? "Attendance bonus rule activated successfully."
          : "Attendance bonus rule deactivated successfully.",
      );

      await getRules();
    } catch (error) {
      console.error("Error updating rule status:", error);

      toast.error(
        getErrorMessage(
          error,
          "Unable to update attendance bonus rule status.",
        ),
      );
    }
  };

  /* =========================================================
     SEARCH AND STATUS FILTER
     ========================================================= */

  const filteredData = rules.filter((item) => {
    const searchText = search.toLowerCase().trim();

    const matchesSearch =
      !searchText ||
      item.gradeCode.toLowerCase().includes(searchText) ||
      item.ruleType.toLowerCase().includes(searchText) ||
      item.criteriaDescription.toLowerCase().includes(searchText);

    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && item.isActive) ||
      (statusFilter === "Inactive" && !item.isActive);

    return matchesSearch && matchesStatus;
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <PageContainer
      title="Attendance Bonus Rule Master"
      description="Manage attendance bonus grade criteria"
    >
      <Breadcrumb title="Attendance Bonus Rule Master" items={BCrumb} />

      {/* =====================================================
          ADD / EDIT FORM
          ===================================================== */}

      <Card sx={{ p: 0, mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="calendar" width="18" />

          <Box sx={{ ml: 1 }}>Attendance Bonus Rule Master</Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#111827",
              mb: 2,
            }}
          >
            {isEdit
              ? "Edit Attendance Bonus Rule"
              : "Add Attendance Bonus Rule"}
          </Typography>

          {pageError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setPageError("")}
            >
              {pageError}
            </Alert>
          )}

          {/* {!isEdit && availableGradeCodes.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              All six grades are already configured. Select Edit
              from a rule below to modify its criteria.
            </Alert>
          )} */}

          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container spacing={2}>
              {/* Grade */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Grade"
                  name="gradeCode"
                  value={formik.values.gradeCode}
                  onChange={(event) => {
                    formik.setFieldValue(
                      "gradeCode",
                      event.target.value.toUpperCase(),
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.gradeCode && Boolean(formik.errors.gradeCode)
                  }
                  helperText={
                    formik.touched.gradeCode
                      ? formik.errors.gradeCode
                      : "Enter a new grade, such as F or G."
                  }
                  inputProps={{
                    maxLength: 5,
                  }}
                  disabled={isEdit}
                />
              </Grid>

              {/* Rule Type */}

              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={
                    formik.touched.ruleType && Boolean(formik.errors.ruleType)
                  }
                >
                  <InputLabel id="bonus-rule-type-label">Rule Type</InputLabel>

                  <Select
                    labelId="bonus-rule-type-label"
                    name="ruleType"
                    value={formik.values.ruleType}
                    label="Rule Type"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {ruleTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>

                  {formik.touched.ruleType && formik.errors.ruleType && (
                    <FormHelperText>{formik.errors.ruleType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Evaluation Order */}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Evaluation Order"
                  name="evaluationOrder"
                  value={formik.values.evaluationOrder}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.evaluationOrder &&
                    Boolean(formik.errors.evaluationOrder)
                  }
                  helperText={
                    formik.touched.evaluationOrder
                      ? formik.errors.evaluationOrder
                      : "Lower values are evaluated first."
                  }
                  inputProps={{
                    min: 1,
                    max: 255,
                    step: 1,
                  }}
                />
              </Grid>

              {/* Leave Weeks */}

              {formik.values.ruleType === "LEAVE_WEEKS" && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    type="number"
                    label="Leave Weeks Required"
                    name="leaveWeeksRequired"
                    value={formik.values.leaveWeeksRequired}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.leaveWeeksRequired &&
                      Boolean(formik.errors.leaveWeeksRequired)
                    }
                    helperText={
                      formik.touched.leaveWeeksRequired
                        ? formik.errors.leaveWeeksRequired
                        : "Valid values: 1 to 4 weeks."
                    }
                    inputProps={{
                      min: 1,
                      max: 4,
                      step: 1,
                    }}
                  />
                </Grid>
              )}

              {/* Advance Leave Count */}

              {formik.values.ruleType === "ADVANCE_LEAVE_ONLY" && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      type="number"
                      label="Required Advance Leave Count"
                      name="requiredAdvanceLeaveCount"
                      value={formik.values.requiredAdvanceLeaveCount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.requiredAdvanceLeaveCount &&
                        Boolean(formik.errors.requiredAdvanceLeaveCount)
                      }
                      helperText={
                        formik.touched.requiredAdvanceLeaveCount
                          ? formik.errors.requiredAdvanceLeaveCount
                          : ""
                      }
                      inputProps={{
                        min: 1,
                        max: 255,
                        step: 1,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      type="number"
                      label="Allowed Other Leave Count"
                      name="allowedOtherLeaveCount"
                      value={formik.values.allowedOtherLeaveCount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.allowedOtherLeaveCount &&
                        Boolean(formik.errors.allowedOtherLeaveCount)
                      }
                      helperText={
                        formik.touched.allowedOtherLeaveCount
                          ? formik.errors.allowedOtherLeaveCount
                          : "Set to 0 when no other leave is allowed."
                      }
                      inputProps={{
                        min: 0,
                        max: 255,
                        step: 1,
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Perfect Attendance Information */}

              {formik.values.ruleType === "PERFECT_ATTENDANCE" && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    This rule represents zero leave during the month. The month
                    must be evaluated using exactly four weeks.
                  </Alert>
                </Grid>
              )}

              {/* Criteria Description */}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  maxRows={5}
                  size="small"
                  label="Criteria Description"
                  name="criteriaDescription"
                  value={formik.values.criteriaDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.criteriaDescription &&
                    Boolean(formik.errors.criteriaDescription)
                  }
                  helperText={
                    formik.touched.criteriaDescription
                      ? formik.errors.criteriaDescription
                      : ""
                  }
                  placeholder="Describe the attendance bonus condition..."
                  inputProps={{
                    maxLength: 500,
                  }}
                />
              </Grid>

              {/* Buttons */}

              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  // startIcon={
                  //   saving ? (
                  //     <CircularProgress size={16} color="inherit" />
                  //   ) : isEdit ? (
                  //     <Edit />
                  //   ) : (
                  //     <Add />
                  //   )
                  // }
                  disabled={saving}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#ff8b70",
                    borderRadius: "5px",
                    minWidth: 100,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#ff795b",
                      boxShadow: "none",
                    },
                  }}
                >
                  {saving ? "Saving..." : isEdit ? "Update" : "Add"}
                </Button>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                  sx={{
                    textTransform: "none",
                  }}
                >
                  {isEdit ? "Cancel" : "Reset"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* =====================================================
          RULE LIST
          ===================================================== */}

      <Card sx={{ p: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="list" width="18" />

          <Box sx={{ ml: 1 }}>Attendance Bonus Rule List</Box>
        </Box>

        <CardContent>
          <Grid container spacing={2} mb={2} alignItems="center">
            {/* Search */}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search grade, rule type, criteria..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <Search
                      sx={{
                        mr: 1,
                        color: "text.secondary",
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="bonus-status-label">Status</InputLabel>

                <Select
                  labelId="bonus-status-label"
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Refresh */}

            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Button
                variant="outlined"
                onClick={getRules}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>No.</b>
                  </TableCell>
                  <TableCell>
                    <b>Grade</b>
                  </TableCell>
                  <TableCell>
                    <b>Rule Type</b>
                  </TableCell>
                  <TableCell>
                    <b>Criteria</b>
                  </TableCell>
                  <TableCell>
                    <b>Order</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Action</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No attendance bonus rules found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {item.gradeCode}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {ruleTypeOptions.find(
                          (option) => option.value === item.ruleType,
                        )?.label || item.ruleType}
                      </TableCell>

                      <TableCell
                        sx={{
                          minWidth: 280,
                          maxWidth: 500,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.criteriaDescription || "-"}
                      </TableCell>

                      <TableCell>{item.evaluationOrder}</TableCell>

                      {/* STATUS + EDIT */}

                      <TableCell align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          gap={1}
                        >
                          <Tooltip
                            title={item.isActive ? "Active" : "Inactive"}
                          >
                            <Switch
                              checked={item.isActive}
                              size="small"
                              onChange={(event) =>
                                handleStatusChange(item, event.target.checked)
                              }
                            />
                          </Tooltip>

                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEdit(item)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default AttendanceBonusRuleMaster;
